/**
 * DNACore Mobile V4.1 — Componentes de paridade visual reutilizáveis.
 *
 * Uso:
 *
 *   import { DnaResponsiveTable, DnaMobileViewToggle, DnaMobileFilterSheet, DnaExportBar, useIsMobileV41 } from '@/components/dna-mobile/DnaResponsive';
 *
 *   // 1) Tabela com scroll horizontal + 1ª col sticky
 *   <DnaResponsiveTable stickyFirstCol>
 *     <table>...</table>
 *   </DnaResponsiveTable>
 *
 *   // 2) Toggle Tabela ↔ Cards (controlled)
 *   <DnaMobileViewToggle view={view} onChange={setView} />
 *   {view === 'table' ? <TableView/> : <CardsView/>}
 *
 *   // 3) Bottom sheet de filtros
 *   <DnaMobileFilterSheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtros">
 *     {/_ controles de filtro _/}
 *   </DnaMobileFilterSheet>
 *
 *   // 4) Barra de exportação mobile-friendly (sempre visível)
 *   <DnaExportBar onExcel={...} onPdf={...} onPrint={...} />
 */
import React, { useEffect, useState } from 'react';
import { Table as TableIcon, LayoutGrid, X, FileSpreadsheet, FileText, Printer } from 'lucide-react';
import './DnaResponsive.css';

// Hook de mobile (cópia para evitar dependência cíclica com DnaCommandCenter)
export function useIsMobileV41(breakpoint = 1024) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export function DnaResponsiveTable({ children, stickyFirstCol = false, dark = false, className = '', testId = 'dna-responsive-table' }) {
  const cls = ['dna-responsive-table',
    stickyFirstCol ? 'sticky-first-col' : '',
    dark ? 'dna-dark' : '',
    className].filter(Boolean).join(' ');
  return <div className={cls} data-testid={testId}>{children}</div>;
}

export function DnaMobileViewToggle({ view, onChange, dark = false, labels = { table: 'Tabela', cards: 'Cards' }, testIdPrefix = 'dna-view' }) {
  return (
    <div className={`dna-view-toggle ${dark ? 'dna-dark' : ''}`} role="tablist">
      <button
        className={view === 'table' ? 'active' : ''}
        onClick={() => onChange('table')}
        data-testid={`${testIdPrefix}-toggle-table`}
        type="button"
      >
        <TableIcon className="w-4 h-4" /> {labels.table}
      </button>
      <button
        className={view === 'cards' ? 'active' : ''}
        onClick={() => onChange('cards')}
        data-testid={`${testIdPrefix}-toggle-cards`}
        type="button"
      >
        <LayoutGrid className="w-4 h-4" /> {labels.cards}
      </button>
    </div>
  );
}

export function DnaMobileFilterSheet({ open, onClose, title = 'Filtros', dark = false, children, footer = null, testId = 'dna-filter-sheet' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="dna-filter-sheet-overlay" onClick={onClose} data-testid={`${testId}-overlay`}>
      <div className={`dna-filter-sheet ${dark ? 'dna-dark' : ''}`} onClick={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="dna-filter-sheet-handle" />
        <div className="dna-filter-sheet-head">
          <div className="dna-filter-sheet-title">{title}</div>
          <button className="dna-filter-sheet-close" onClick={onClose} data-testid={`${testId}-close`} aria-label="Fechar" type="button">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
        {footer && <div style={{ marginTop: 16 }}>{footer}</div>}
      </div>
    </div>
  );
}

export function DnaExportBar({ onExcel, onPdf, onPrint, onCustom = [], align = 'flex-start' }) {
  if (!onExcel && !onPdf && !onPrint && onCustom.length === 0) return null;
  return (
    <div className="dna-export-bar" style={{ justifyContent: align }}>
      {onExcel && (
        <button className="dna-export-btn excel" onClick={onExcel} data-testid="dna-export-excel" type="button">
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </button>
      )}
      {onPdf && (
        <button className="dna-export-btn pdf" onClick={onPdf} data-testid="dna-export-pdf" type="button">
          <FileText className="w-4 h-4" /> PDF
        </button>
      )}
      {onPrint && (
        <button className="dna-export-btn print" onClick={onPrint} data-testid="dna-export-print" type="button">
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      )}
      {onCustom.map((c, i) => (
        <button key={i} className="dna-export-btn" onClick={c.onClick} data-testid={c.testId || `dna-export-custom-${i}`} type="button">
          {c.icon}{c.label}
        </button>
      ))}
    </div>
  );
}

// Card mobile genérico para alternativa visual em tabelas pesadas
export function DnaMobileCard({ rows = [], dark = false, footer = null, onClick, testId }) {
  return (
    <div className={`dna-mobile-card ${dark ? 'dna-dark' : ''}`} onClick={onClick} data-testid={testId} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      {rows.map((r, i) => (
        <div key={i} className="dna-mobile-card-row">
          <span className="lbl">{r.label}</span>
          <span className="val">{r.value}</span>
        </div>
      ))}
      {footer && <div style={{ marginTop: 8 }}>{footer}</div>}
    </div>
  );
}

export function DnaMobileCards({ items = [], renderCard, dark = false }) {
  return (
    <div className="dna-mobile-cards">
      {items.map((it, i) => (
        renderCard ? renderCard(it, i) : <DnaMobileCard key={i} rows={Object.entries(it).map(([k, v]) => ({ label: k, value: String(v) }))} dark={dark} />
      ))}
    </div>
  );
}
